import * as iconData from '@maui/icon-data'
import env from '@maui-mf/env'
import { api } from '@maui-mf/common-auth'
import { triggerAppChange, registerApplication, start as startSingleSpa } from 'single-spa'
import './trackers'
import * as authFailure from './auth-failure'
import * as featureFlags from './feature-flags'
import * as gti from './gti'
import * as replaceTrackingParams from './replace-tracking-params'
import { buildRoutes } from './routes'
import { parsePackageSpecifier } from './utils/parsePackageSpecifier'
import { ready as authReady } from './auth-store'

// TODO dashboards organization has a lot of issues because of the hasty extraction of
// @mandiant-mfe/dashboards out of what was then a single app. Here we need to preload this because
// it contains context and it's not a singleton. It's not a great solution, we need to refactor
// maui-mf/app-dashboards, mandiant-mfe/dashboards, and the maui/dashboards feature package.
// It's likely that the mfe dashboards package will no longer exist and it'll just be up to the
// root app to instantiate the provider.
import '@maui/dashboards'

// NOTE we're injecting styles instead of importing from @maui/elements-global to keep the single
// interface to the maui elements bundle through the remote. It'd probably be possible to export
// CSS as a remote (maybe?) but it's not currently set up that way.
import { injectGlobalStyles } from '@maui/elements'
import './styles.css'
injectGlobalStyles()

// Workaround for bug in single-spa-layout which is attempting some kind of cjs/esm interop & failing.
// Specifically it will throw a TypeError complaining about reading pathToActiveWhen of undefined, which
// you can see is happening incorrectly in the "fixed" source.
//
// By `require`ing we force the import of the CJS version via the package.json `exports` mapping. Note
// this is the only reasonable way to import this file as the package.json's exports preclude otherwise
// importing dist.
//
// See: https://github.com/single-spa/single-spa-layout/issues/93
const { constructApplications, constructRoutes, constructLayoutEngine } = require('single-spa-layout')

window.__maui_icon_data__ = iconData
window.__maui_icon_prefer_font__ = true

const moduleIsContainer = module => {
  return Object.keys(module).length === 2 &&
    typeof module.get === 'function' &&
    typeof module.init === 'function'
}

const featureMap = new Map()
const shareScope = 'default'

/**
 * A proxy wrapper around the route objects that single-spa expects for route configuration,
 * but extending the activeWhen setter function to respect split features.
 *
 * This is done with a proxy because singl-spa-layout's `activeWhen` definition happens inside
 * the route creation and directly sets `activeWhen`, then references it internally to build
 * the default route's behavior to only render if no siblings are active. This required(?) a
 * slightly creative workaround here to intercept the assignment of the function to wrap it with
 * our own function on set.
 *
 * The concept of `splits` and `notsplits` felt necessary as besides the default route, other
 * sibling routes will still render regardless of how many are active. Defining the splits as sets
 * of mutually exclusive routes seemed the simpler solution than further hacking on single-spa.
 */
class AppRoute {
  constructor(obj) {
    for (const key in obj) {
      this[key] = obj[key]
    }
  }

  get activeWhen() {
    return super.activeWhen
  }

  set activeWhen(fn) {
    super.activeWhen = location => {
      const meta = this.props?.__meta || {}

      // If we have splits...
      if (Array.isArray(meta.splits)) {
        // Get the first active split name which is enabled in the feature map
        const activeSplit = meta.splits.find(split => featureMap.get(split))

        // If the index of this route's split isn't the same as the first matching split, return
        // false. Note this behavior is specifically intended to only match the first matching
        // split, making the splits in the config order dependent with the first matching split
        // winning. It's also covering the fallback route, which has a split of undefined, which
        // will match a failed find for activeSplit.
        if (activeSplit !== meta.split) {
          return false
        }
      }

      return fn(location)
    }
  }
}

const splitAppRoutes = app => {
  const splits = app.splits || []

  return [...splits, app].map(({ package_name: name, split }) => {
    return new AppRoute({
      type: 'route',
      exact: !!app.exact,
      path: app.path,
      props: {
        ...app.props,
        __meta: {
          ...parsePackageSpecifier(name),
          ...(app.splits && {
            split,
            splits: splits.map(s => s.split),
          })
        }
      },
      routes: [
        { type: 'application', name }
      ],
    })
  })
}

const createRoutes = (appRoutes, variant = 'default') => {
  const splitRoutes = appRoutes.reduce((config, app) => {
    const name = app.package_name

    if (!app.path && app.splits) {
      throw new Error('splits are only supported non-default path routed apps')
    }

    if (app.default) {
      config.routed.push({
        type: 'route',
        default: true,
        routes: [
          { type: 'application', name }
        ],
      })
    } else if (app.path) {
      config.routed.push(...splitAppRoutes(app))
    } else {
      config.unrouted.push({
        type: 'application',
        name,
      })
    }

    return config
  }, {
    routed: [],
    unrouted: [],
  })

  return constructRoutes({
    routes: buildRoutes(variant, splitRoutes)
  })
}

const loadApp = async ({ name }) => {
  const { entryPath, packageName } = parsePackageSpecifier(name)

  const mod = await System.import(packageName)

  if (!moduleIsContainer(mod)) {
    return mod
  }

  await __webpack_init_sharing__(shareScope)
  await mod.init(__webpack_share_scopes__[shareScope])
  const factory = await mod.get(entryPath)
  return factory()
}

const createApplications = (routes) => {
  const applications = constructApplications({ routes, loadApp })
  return applications
}

const applyRouteFeatures = (features = {}, refreshRoutes) => {
  featureMap.clear()

  for (const feature in features) {
    featureMap.set(feature, features[feature])
  }

  refreshRoutes && triggerAppChange()
}

const getUxVariant = user => gti.wantsFeature(user) ? 'gti' : 'default'

export const start = async (options = {}) => {
  const store = await authReady

  if (gti.shouldOverrideSession(store)) {
    api.logout({ host: env.MAUI_IDP_HOST, gtiLogin: true })
    return
  }

  const user = store.getValue()
  const isLoggedIn = !!user

  featureFlags.start()
  authFailure.start(store)
  replaceTrackingParams.start()

  const routes = createRoutes(options.routes, getUxVariant(user))
  const applications = createApplications(routes)

  // This deserves explanation.
  //
  // The (a) trick to loading routes based on feature flags is that of course if there's no user,
  // there are no flags. On initial load, if not logged in the only app that's being mounted
  // (outside weird circumstances) is the auth app, so no features are needed.
  //
  // However there's still no user on initial load of the app after returning from SSO login, as
  // the code that exchanges the code for a JWT and initializes the auth state is in the auth app.
  if (isLoggedIn) {
    // With that in mind, on load if we're already logged in, i.e. there's already a user present
    // in localstorage, then we know feature flags will resolve almost immediately, and we just
    // wait for it to finish before loading the routes.
    const features = await featureFlags.getFeatures()

    if (gti.assertLoadable(user, features)) {
      applyRouteFeatures(features)
    }
  } else {
    // However if we're not logged in yet, we still wait for treatments but we don't block on it.
    // When login occurs the feature flags will be returned shortly after, and at that time we
    // apply the features to the route logic and `singleSpa.triggerAppChange()`, which forces
    // the mounted app state to refresh without a history event.
    featureFlags.getFeatures().then(features => {
      if (gti.assertLoadable(user, features)) {
        applyRouteFeatures(features, true)
      }
    })
  }

  applications.forEach(registerApplication)
  constructLayoutEngine({ applications, routes })
  startSingleSpa()
}
